import { InputGroup } from "@blueprintjs/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RuleNumberArray } from "../../../src/types/rule";

function toNumberArray(str: string): number[] {
    // verify the input is a valid array
    const valueAsNumberArray = JSON.parse(`[${str}]`);

    // verify the array is all numbers
    for (const entry of valueAsNumberArray) {
        if (!Number.isInteger(entry)) {
            throw new Error();
        }
    }
    return valueAsNumberArray;
}

type Props = {
    isDisabled?: boolean;
    isEditable: boolean;
    onChange: (any) => void;
    rule: RuleNumberArray;
    value: number[];
};

export default function GameRuleNumberArray({
    isDisabled,
    isEditable,
    onChange,
    rule,
    value,
}: Props) {
    const [localValue, setLocalValue] = useState<string>(value.join(', '));
    const timerId = useRef<ReturnType<typeof setTimeout> | null>(null);
    const inputRef = useRef<HTMLInputElement>();

    const parsedValue = useMemo(
        () => {
            try {
                return toNumberArray(localValue);
            } catch {
                return null;
            }
        },
        [localValue],
    );

    const isError = parsedValue === null;

    const isNewValues = useCallback(
        (valueAsNumberArray: number[]): boolean => {
            // verify the array is different from the passed in array
            if (value.length === valueAsNumberArray.length && value.every(
                (v, idx) => v === valueAsNumberArray[idx]
            )) {
                return false;
            }
            return true;
        },
        [value],
    );

    useEffect(
        () => {
            if (parsedValue === null || !isNewValues(parsedValue)) {
                return;
            }

            // Save after a second without changes, or immediately on blur.
            const timer = setTimeout(
                () => {
                    timerId.current = null;
                    onChange(parsedValue);
                },
                1000,
            );

            timerId.current = timer;

            return () => {
                clearTimeout(timer);
                timerId.current = null;
            };
        },
        [parsedValue, onChange, isNewValues],
    );

    const onBlur = useCallback(
        () => {
            if (timerId.current !== null) {
                clearTimeout(timerId.current);
                timerId.current = null;
            }

            if (parsedValue === null) {
                inputRef.current?.focus();
            } else if (isNewValues(parsedValue)) {
                onChange(parsedValue);
            }
        },
        [isNewValues, parsedValue, onChange],
    );

    return (
        <InputGroup
            disabled={isDisabled || !isEditable}
            id={rule.key}
            inputRef={inputRef}
            intent={isError ? 'danger' : 'none'}
            onValueChange={(v) => setLocalValue(v)}
            onBlur={onBlur}
            value={localValue}
        />
    );
}
